/*
 * "Open support issues" — the assistant's report on the Issues list (the issue tracker), as
 * opposed to pump faults. Runs in the console against the tracker API the Issues page uses
 * (GET /issues/meta + /issues), so it needs no server function. Not pinnable to the Dashboard
 * yet: pins re-run reports on the server, which only knows the fleet reports.
 */

import { appRest } from './apiClient';
import type { ReportParams, ReportResult } from './reports';

interface Named { id: string; key: string; name: string; rank?: number; statusCategory?: string }
interface Meta { types: Named[]; severities: Named[]; statuses: Named[] }
interface IssueRow {
  id: string;
  key?: string;
  issueNumber?: number;
  title: string;
  typeId: string;
  severityId: string;
  statusId: string;
  assigneeUserId: string | null;
  createdAt: string;
  updatedAt?: string;
}

const TERMINAL = new Set(['resolved', 'closed', 'wont_fix']);
const DAY = 86_400_000;

function ageDays(iso: string, now: number): number | null {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / DAY)) : null;
}

/** Open = not in a terminal status category. Most severe first (lower severity rank), then oldest. */
export function buildSupportIssues(meta: Meta, items: IssueRow[], params: ReportParams = {}, now = Date.now()): ReportResult {
  const status = new Map(meta.statuses.map((s) => [s.id, s]));
  const severity = new Map(meta.severities.map((s) => [s.id, s]));
  const type = new Map(meta.types.map((t) => [t.id, t]));
  const open = items.filter((i) => {
    const s = status.get(i.statusId);
    return !TERMINAL.has(s?.statusCategory ?? '');
  });
  const sevRank = (i: IssueRow) => severity.get(i.severityId)?.rank ?? 999;
  open.sort((a, b) => sevRank(a) - sevRank(b) || Date.parse(a.createdAt) - Date.parse(b.createdAt));
  const rows = open.map((i) => ({
    id: i.id,
    key: i.key || (i.issueNumber !== undefined ? `#${i.issueNumber}` : i.title.slice(0, 40) || i.id.slice(0, 8)),
    title: i.title,
    severity: severity.get(i.severityId)?.name ?? '—',
    status: status.get(i.statusId)?.name ?? '—',
    type: type.get(i.typeId)?.name ?? '—',
    assigned: i.assigneeUserId ? 'yes' : 'no',
    age_days: ageDays(i.createdAt, now),
    updated_at: i.updatedAt ?? null,
  }));
  const unassigned = rows.filter((r) => r.assigned === 'no').length;
  return {
    report: 'support_issues',
    title: 'Open support issues',
    subtitle: rows.length
      ? `${rows.length} open on the Issues list · ${unassigned} unassigned · most severe first`
      : 'Nothing open on the Issues list',
    params,
    columns: [
      { key: 'key', label: 'Issue', type: 'text', link: { kind: 'issue', idKey: 'id' } },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'severity', label: 'Severity', type: 'text' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'type', label: 'Type', type: 'text' },
      { key: 'assigned', label: 'Assigned', type: 'text' },
      { key: 'age_days', label: 'Age (days)', type: 'number' },
      { key: 'updated_at', label: 'Last update', type: 'datetime' },
    ],
    rows,
    chart: null,
    generatedAt: new Date(now).toISOString(),
  };
}

/** Every issue: the server clamps a page to 200 rows, so walk offset → total (dedupe by id, as the Issues page does). */
async function listAllIssues(): Promise<IssueRow[]> {
  const seen = new Map<string, IssueRow>();
  let offset = 0;
  for (let page = 0; page < 50; page++) {
    const res = await appRest<{ items?: IssueRow[]; total?: number }>('GET', `/issues?limit=200&offset=${offset}`);
    const batch = res.items ?? [];
    for (const it of batch) if (!seen.has(it.id)) seen.set(it.id, it);
    offset += batch.length;
    if (batch.length === 0 || offset >= (res.total ?? 0)) break;
  }
  return [...seen.values()];
}

export async function runSupportIssues(params: ReportParams = {}): Promise<ReportResult> {
  const [meta, items] = await Promise.all([appRest<Meta>('GET', '/issues/meta'), listAllIssues()]);
  return buildSupportIssues(meta, items, params);
}
